/**
 * FilePreviewModal Component
 * Modal for previewing files (images, PDFs, videos)
 */

import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/overlays';
import { Center, Stack } from '@/components/ui/layout';
import { Loader } from '@/components/ui/feedback';
import { Text } from '@/components/ui/core';
import { Image } from '@mantine/core';
import { FileRecordModelView } from '../types';
import { useS3Operations } from '../hooks/useS3Operations';

interface FilePreviewModalProps {
  file: FileRecordModelView | null;
  opened: boolean;
  onClose: () => void;
}

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  file,
  opened,
  onClose,
}) => {
  const s3Operations = useS3Operations();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (file && opened) {
      setLoading(true);
      s3Operations.previewFile(file.id).then((url) => {
        setPreviewUrl(url);
        setLoading(false);
      });
    }
  }, [file, opened, s3Operations]);

  if (!file) return null;

  const isImage = file.mimeType.startsWith('image/');
  const isVideo = file.mimeType.startsWith('video/');
  const isPDF = file.mimeType.includes('pdf');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={file.fileName}
      size="xl"
    >
      {loading ? (
        <Center p="xl">
          <Loader />
        </Center>
      ) : (
        <Stack gap="md">
          {isImage && previewUrl && (
            <Image
              src={previewUrl}
              alt={file.fileName}
              fit="contain"
              height={600}
            />
          )}

          {isVideo && previewUrl && (
            <video
              src={previewUrl}
              controls
              style={{ width: '100%', maxHeight: 600 }}
            />
          )}

          {isPDF && previewUrl && (
            <iframe
              src={previewUrl}
              title={file.fileName}
              style={{ width: '100%', height: 600, border: 'none' }}
            />
          )}

          {!isImage && !isVideo && !isPDF && (
            <Center p="xl">
              <Text c="dimmed">
                Preview not available for this file type
              </Text>
            </Center>
          )}
        </Stack>
      )}
    </Modal>
  );
};
